import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownCircle, CheckCircle2, Clock3, RefreshCw, ReceiptText, Wallet as WalletIcon, XCircle } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WalletHeroCard from "../components/WalletHeroCard";
import { CashbackPanel } from "../components/Cashback";
import IdentityVerificationDialog from "../components/IdentityVerificationDialog";

const AUTO_CHECKOUT_PROVIDERS = new Set(["keyhub", "nexa", "nowpayments", "playhubslots"]);

export default function WalletPage() {
  const { user, refreshUser } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [diditOpen, setDiditOpen] = useState(() => new URLSearchParams(window.location.search).has("didit"));
  const userReady = Boolean(user && user !== false);

  const load = useCallback(async () => {
    if (!userReady) return;
    setLoading(true);
    try {
      const reqRes = await api.get("/me/requests");
      setRequests(reqRes.data || []);
    } finally {
      setLoading(false);
    }
  }, [userReady]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const visibleRequests = useMemo(() => {
    return requests.filter((row) => !isPassiveCheckoutPending(row));
  }, [requests]);

  const totals = useMemo(() => {
    return visibleRequests.reduce((acc, row) => {
      if (row.status === "pending") acc.pending += 1;
      if (row.status === "approved" && isWalletIncoming(row)) acc.in += row.amount || 0;
      if (row.status === "approved" && isWalletOutgoing(row)) acc.out += row.amount || 0;
      return acc;
    }, { in: 0, out: 0, pending: 0 });
  }, [visibleRequests]);

  if (!user) {
    return (
      <div className="min-h-screen bg-[#030705] text-white">
        <Navbar />
        <main className="mx-auto max-w-md px-6 py-32 text-center">
          <h1 className="font-display text-3xl font-bold">Sign in to open your wallet</h1>
          <Link to="/login" className="mt-6 inline-flex rounded-full bg-[#D4AF37] px-8 py-3 font-bold text-black">Sign in</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-clip bg-[#030705] text-white">
      <Navbar />
      <main className="mx-auto w-full max-w-7xl px-4 pb-32 pt-8 sm:px-6 md:px-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">Wallet</p>
            <h1 className="font-display text-3xl font-black md:text-4xl">Manage your money</h1>
          </div>
          <button
            type="button"
            onClick={() => {
              refreshUser?.();
              load().catch(() => {});
            }}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-bold hover:bg-white/5 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <WalletHeroCard
          user={user}
          className="mb-6"
          onBalanceChange={() => {
            refreshUser?.();
            load().catch(() => {});
          }}
        />

        <CashbackPanel refreshKey={requests} onClaim={async () => { await refreshUser?.(); await load(); }} />

        <section className="mb-6 grid gap-3 md:grid-cols-3">
          <Stat icon={WalletIcon} label="Approved added" value={money(totals.in)} tone="emerald" />
          <Stat icon={ArrowDownCircle} label="Approved withdrawn" value={money(totals.out)} tone="amber" />
          <Stat icon={Clock3} label="Pending reviews" value={totals.pending.toLocaleString("en-US")} tone="slate" />
        </section>

        <p className="mb-6 text-sm text-white/50">
          After 15 days without signing in or using the website, your website cash wallet and freeplay balances expire and become zero. Your game balances stay unchanged.
        </p>

        <section className="grid gap-6">
          <div className="rounded-2xl border border-white/10 bg-[#0A130E] p-5">
            <div className="mb-4 flex items-center gap-2">
              <ReceiptText className="h-4 w-4 text-[#D4AF37]" />
              <h2 className="font-display text-xl font-bold">Wallet history</h2>
            </div>
            <div className="space-y-3">
              {visibleRequests.map((row) => (
                <TransactionRow key={row.id} row={row} />
              ))}
              {visibleRequests.length === 0 && (
                <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-white/45">
                  No wallet activity yet.
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <IdentityVerificationDialog open={diditOpen} onClose={() => { setDiditOpen(false); window.history.replaceState(null, "", window.location.pathname); }} onSubmitted={() => refreshUser?.()} user={user} />
      <Footer />
    </div>
  );
}

function TransactionRow({ row }) {
  const isOut = ["redeem", "admin_debit", "wallet_transfer_sent", "inactivity_expiry"].includes(row.type);
  const Icon = row.status === "approved" ? CheckCircle2 : row.status === "rejected" ? XCircle : Clock3;
  return (
    <div className="w-full max-w-full overflow-hidden rounded-xl border border-white/10 bg-black/25 p-4">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="min-w-0 break-words font-bold capitalize">{labelType(row.type)}</span>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${statusTone(row.status)}`}>
              <Icon className="h-3 w-3" />
              {row.status}
            </span>
          </div>
          <p className="mt-1 break-words text-sm text-white/50">{walletNote(row)}</p>
          <p className="mt-2 text-xs text-white/35">{row.created_at ? new Date(row.created_at).toLocaleString("en-US") : ""}</p>
        </div>
        <div className={`w-full min-w-0 text-left font-display text-lg font-black tabular-nums sm:w-auto sm:shrink-0 sm:text-right sm:text-xl ${isOut ? "text-amber-300" : "text-emerald-300"}`}>
          {isOut ? "-" : "+"}{money(row.amount || 0)}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }) {
  const tones = {
    emerald: "border-emerald-700/30 text-emerald-300",
    amber: "border-amber-700/30 text-amber-300",
    slate: "border-white/10 text-white",
  };
  return (
    <div className={`rounded-2xl border bg-[#0A130E] p-5 ${tones[tone]}`}>
      <Icon className="mb-3 h-5 w-5" />
      <div className="text-xs uppercase tracking-[0.18em] text-white/45">{label}</div>
      <div className="mt-1 font-display text-2xl font-black tabular-nums">{value}</div>
    </div>
  );
}

function labelType(type = "") {
  const labels = {
    wallet_transfer_sent: "Transfer sent",
    wallet_transfer_received: "Transfer received",
    inactivity_expiry: "Inactive wallet balance expired",
    cashback_claim: "Deposit cashback",
    referral_cashback_claim: "Referral cashback",
  };
  return labels[type] || type.replace(/_/g, " ");
}

function walletNote(row) {
  const text = row.note || row.admin_note || "No note";
  return String(text)
    .replace(/\bkey\s*hub\b/gi, "checkout")
    .replace(/\bkeyhub\b/gi, "checkout")
    .replace(/\s+/g, " ")
    .trim();
}

function isWalletIncoming(row) {
  if (!row) return false;
  if (["admin_credit", "wallet_transfer_received"].includes(row.type)) return true;
  if (row.type !== "load") return false;
  if (row.game_id || row.gameId || row.gameName || row.game_name || row.transfer_kind === "game_load") return false;
  if (row.wallet_debit_amount || row.cash_wallet_debit_amount || row.free_play_debit_amount || row.provider_amount) return false;
  if (row.payment_provider || row.payment_platform || row.payment_method_id || row.gateway_transaction_id) return true;
  const note = String(row.note || row.admin_note || "").toLowerCase();
  if (note.includes("website game load") || note.includes("game load:") || note.includes("source wallet") || note.includes("source freeplay")) return false;
  return ["add money", "deposit", "checkout", "payment", "cash app", "cashapp", "chime", "venmo", "paypal", "card", "google pay", "apple pay"].some((term) => note.includes(term));
}

function isWalletOutgoing(row) {
  if (!row) return false;
  if (["admin_debit", "wallet_transfer_sent"].includes(row.type)) return true;
  if (row.type !== "redeem") return false;
  if (row.game_id || row.gameId || row.gameName || row.game_name) return false;
  if (["game_redeem", "game_load"].includes(row.transfer_kind)) return false;
  return true;
}

function isPassiveCheckoutPending(row) {
  if (!row || row.type !== "load" || row.status !== "pending") return false;
  const provider = String(row.payment_provider || "").trim().toLowerCase();
  if (!AUTO_CHECKOUT_PROVIDERS.has(provider)) return false;
  if (row.payment_mismatch) return false;
  const providerStatus = String(row.provider_status || "").trim().toLowerCase();
  if (["manual_review", "review_required", "requires_review"].includes(providerStatus)) return false;
  const adminNote = String(row.admin_note || "").toLowerCase();
  return !adminNote.includes("manual review") && !adminNote.includes("requires review");
}

function statusTone(status) {
  if (status === "approved") return "border-emerald-700/30 bg-emerald-900/20 text-emerald-300";
  if (status === "rejected") return "border-red-700/30 bg-red-900/20 text-red-300";
  return "border-white/10 bg-white/5 text-white/60";
}

function money(coins) {
  return `$${((coins || 0) / 1000).toFixed(2)}`;
}
